import React from 'react';
import { 
  Users, 
  BarChart3, 
  Clock, 
  CheckCircle2,
  Building2,
  PhoneCall,
  Mail,
  Calendar,
  DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function App() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-white" />
              <span className="ml-2 text-2xl font-bold text-white">EZ-CRM</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#features" className="text-white hover:text-blue-200">Features</a>
              <a href="#pricing" className="text-white hover:text-blue-200">Pricing</a>
              <a href="#contact" className="text-white hover:text-blue-200">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/app')}
                className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-400 transition-colors"
              >
                Log In/Sign Up
              </button>
              <button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-24">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2">
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Simplify Your Customer Relationships
              </h1>
              <p className="mt-6 text-xl text-blue-100">
                EZ-CRM helps you manage customer relationships effortlessly. Track leads, close deals, and grow your business with our intuitive platform.
              </p>
              <div className="mt-8 flex space-x-4">
                <button 
                  onClick={() => navigate('/pricing')}
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Start Free Trial
                </button>
                <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors">
                  Watch Demo
                </button>
              </div>
            </div>
            <div className="md:w-1/2 mt-12 md:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                alt="Dashboard Preview"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-16">
            Everything you need to manage customer relationships
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            <FeatureCard 
              icon={<BarChart3 className="h-8 w-8 text-blue-600" />}
              title="Analytics & Reporting"
              description="Get detailed insights into your sales pipeline and customer interactions."
            />
            <FeatureCard 
              icon={<Clock className="h-8 w-8 text-blue-600" />}
              title="Time Management"
              description="Schedule meetings and follow-ups with automated reminders."
            />
            <FeatureCard 
              icon={<Building2 className="h-8 w-8 text-blue-600" />}
              title="Contact Management"
              description="Keep all your customer information organized in one place."
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-16">
            Simple, transparent pricing
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <PricingCard 
              title="Starter"
              price="5"
              features={[
                "Up to 1,000 contacts",
                "Basic reporting",
                "Email integration",
                "5 team members"
              ]}
              onGetStarted={() => navigate('/pricing')}
            />
            <PricingCard 
              title="Professional"
              price="10"
              featured={true}
              features={[
                "Up to 10,000 contacts",
                "Advanced analytics",
                "API access",
                "Unlimited team members"
              ]}
              onGetStarted={() => navigate('/pricing')}
            />
            <PricingCard 
              title="Enterprise"
              price="20"
              features={[
                "Unlimited contacts",
                "Custom reporting",
                "Dedicated support",
                "Custom integrations"
              ]}
              onGetStarted={() => navigate('/pricing')}
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-16">
            Get in touch
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <ContactCard 
                icon={<PhoneCall className="h-6 w-6 text-blue-600" />}
                title="Sales"
                info="+1 (555) 123-4567"
              />
              <ContactCard 
                icon={<Mail className="h-6 w-6 text-blue-600" />}
                title="Support"
                info="support@ez-crm.com"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center">
                <Users className="h-8 w-8" />
                <span className="ml-2 text-2xl font-bold">EZ-CRM</span>
              </div>
              <p className="mt-4 text-gray-400">
                Making customer relationship management easy and efficient.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Privacy</a></li>
                <li><a href="#" className="hover:text-white">Terms</a></li>
                <li><a href="#" className="hover:text-white">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 EZ-CRM. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function PricingCard({ title, price, features, featured = false, onGetStarted }) {
  return (
    <div className={`p-8 rounded-lg ${featured ? 'bg-blue-600 text-white ring-4 ring-blue-200' : 'bg-white'}`}>
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <div className="flex items-baseline mb-8">
        <span className="text-4xl font-bold">${price}</span>
        <span className="ml-2 opacity-75">/user/month</span>
      </div>
      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <CheckCircle2 className={`h-5 w-5 ${featured ? 'text-white' : 'text-blue-600'} mr-2`} />
            {feature}
          </li>
        ))}
      </ul>
      <button 
        onClick={onGetStarted}
        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
          featured 
            ? 'bg-white text-blue-600 hover:bg-blue-50' 
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        Get Started
      </button>
    </div>
  );
}

function ContactCard({ icon, title, info }) {
  return (
    <div className="p-6 bg-white rounded-lg shadow-lg flex items-center">
      <div className="mr-4">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <p className="text-gray-600">{info}</p>
      </div>
    </div>
  );
}

export default App;